import { Warn } from '@utilities/logging';
import { MemoryCache } from '@utilities/memory-cache';

export type EventBusListener = {
  key: string;
  callback: (data?: unknown) => unknown;
  context: any;
  args: any[];
};

export class EventBus {
  listeners = new MemoryCache<string, EventBusListener[]>();

  constructor() {
    const listener = (event: MessageEvent) => {
      if (!event.origin.includes('uefa.com')) return;

      if (event.data.key) this.dispatch(event.data.key, event.data.content);
    };

    window.addEventListener('message', listener, false);
  }

  /**
   * Clear the listeners
   */
  clear() {
    this.listeners.clear();
  }

  /**
   * Register an handler on the eventbus   *
   * @param key The key to call
   * @param callback The handler
   * @param context The context of the handler
   * @param args Additional argumens
   */
  on(key: string | string[], callback: (...params: any[]) => unknown, context?: any, ...args: any[]) {
    if (!key) {
      throw new Error('key must not be null');
    }
    if (!callback) {
      throw new Error('callback must not be null');
    }

    if (Array.isArray(key)) {
      return key.forEach((k) => this.on(k, callback, context));
    }
    const listener = {
      key,
      callback,
      context,
      args,
    } as EventBusListener;

    const keyListeners = this.listeners.getOrSet(key, () => [listener]);

    if (
      keyListeners.some(
        (l) =>
          l.callback.toString() === callback.toString() &&
          args &&
          l.args.toString() === args.toString() &&
          context &&
          Object.is(l.context, context),
      )
    ) {
      return;
    }

    this.listeners.set(key, [...keyListeners, listener]);
  }

  /**
   * Remove one or more listener(s)
   * @param key The key of the listener(s) to remove
   * @param callback The handler
   * @param context The context of the handler
   */
  off(key: string | string[], callback?: (data?: unknown) => unknown, context?: any): boolean {
    if (!key) {
      throw new Error('key must not be null');
    }

    if (!this.listeners?.size) {
      return false;
    }

    if (Array.isArray(key)) {
      return key.some((k) => this.off(k, callback, context));
    }

    const keyListeners = this.listeners
      .get(key)
      ?.filter(
        (listener) =>
          (callback && listener.callback.toString() !== callback.toString()) ||
          (context && !Object.is(listener.context, context)),
      );

    if (keyListeners?.length) {
      this.listeners.set(key, keyListeners);
    } else {
      this.listeners.delete(key);
    }

    return !!this.listeners?.get(key)?.length;
  }

  /**
   * Dispatch a key
   * @param key The key name
   * @param args Additional arguments
   * @returns true if the key has been fired
   */
  dispatch(key: string, ...args: any[]): boolean {
    if (!key) {
      throw new Error('key must not be null');
    }

    const keyListeners = this.listeners.get(key);
    if (!keyListeners?.length) {
      return false;
    }

    keyListeners.forEach((listener) => {
      const data = [...listener.args, ...args];
      try {
        listener.callback.apply(listener.context, data);
      } catch (e) {
        Warn(e, listener, data);
      }
    });

    return true;
  }

  /**
   * Check if there are listeners for a key
   * @param key The key name
   * @param target The target of the key
   * @returns true if the key has been fired
   */
  anybody(key: string): boolean {
    if (!key) {
      throw new Error('key must not be null');
    }

    return this.listeners.has(key);
  }

  static readonly instance = new EventBus();
}

const EventBusInstance = EventBus.instance;
const d3 = (window.D3 ??= {});
d3.eventBus ??= EventBusInstance;

export default EventBusInstance;
